import { axiosDelete, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks';
import type { z } from 'zod';
import { invalidateListMap } from '../../../../_utils/query-keys';
import { ExpenditureCartFormSchema } from '../../../_models/expenditure-cart';

const ExpenditureCartMutationParamsSchema = ExpenditureCartFormSchema;

type CreateExpenditureCartType = z.infer<typeof ExpenditureCartFormSchema> & {
  projectId: string;
  subProjectId: string;
  expenditureBudgetPurposesId?: string;
};
export const useCreateExpenditureCart = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      projectId,
      subProjectId,
      ...values
    }: CreateExpenditureCartType) => {
      const payload = ExpenditureCartMutationParamsSchema.parse(values);
      const res = await axiosPost(
        `/api/projects/${projectId}/sub-projects/${subProjectId}/expenditure-carts/`,
        { ...payload },
      );
      await Promise.all(
        invalidateListMap
          .updateDetailedBreakdown(projectId)
          .map(async (invalidateList) => {
            await invalidate(invalidateList);
          }),
      );
      return res;
    },
  });
};

export const useUpdateExpenditureCart = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      id,
      projectId,
      subProjectId,
      ...values
    }: z.infer<typeof ExpenditureCartFormSchema> & {
      id: string;
      projectId: string;
      subProjectId: string;
    }) => {
      const payload = ExpenditureCartMutationParamsSchema.parse(values);
      const res = await axiosPatch(
        `/api/projects/${projectId}/sub-projects/${subProjectId}/expenditure-carts/${id}/`,
        payload,
      );
      await Promise.all(
        invalidateListMap
          .updateDetailedBreakdown(projectId)
          .map(async (invalidateList) => {
            await invalidate(invalidateList);
          }),
      );
      return res;
    },
  });
};

export const useDeleteExpenditureCart = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      id,
      projectId,
      subProjectId,
    }: {
      id: string;
      projectId: string;
      subProjectId: string;
    }) => {
      const res = await axiosDelete(
        `/api/projects/${projectId}/sub-projects/${subProjectId}/expenditure-carts/${id}/`,
      );
      await Promise.all(
        invalidateListMap
          .updateDetailedBreakdown(projectId)
          .map(async (invalidateList) => {
            await invalidate(invalidateList);
          }),
      );
      return res;
    },
  });
};
